import { Badge, Icon } from '@/components';
import type { QuizQuestion } from '@/types/domain';

const TYPE_LABEL: Record<string, string> = {
  single: 'Single choice',
  multiple: 'Select all',
  truefalse: 'True / False',
};

/**
 * One quiz/test question with selectable options. Shared by the course test and
 * the post-lesson check so they feel identical. When `correct` is provided the
 * question locks into review mode — the right answer shows sage ✓, the learner's
 * wrong pick shows clay ✗.
 */
export function QuizQuestionView({
  q,
  selected,
  onPick,
  correct,
}: {
  q: QuizQuestion;
  selected: number | number[] | undefined;
  onPick: (opt: number) => void;
  correct?: number | number[];
}) {
  const multi = q.type === 'multiple';
  const review = correct !== undefined;
  const isSel = (oi: number) => (multi ? Array.isArray(selected) && selected.includes(oi) : selected === oi);
  const isRight = (oi: number) => (Array.isArray(correct) ? correct.includes(oi) : correct === oi);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Badge kind="line">{TYPE_LABEL[q.type] ?? 'Question'}</Badge>
      <div style={{ fontSize: 16.5, fontWeight: 600, lineHeight: 1.42, letterSpacing: '-.01em' }}>{q.stem}</div>
      {q.imageUrl && (
        <img src={q.imageUrl} alt="" style={{ width: '100%', borderRadius: 12, border: '1px solid var(--line)', display: 'block' }} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {q.options.map((opt, oi) => {
          const on = isSel(oi);
          const right = review && isRight(oi);
          const wrong = review && on && !isRight(oi);
          const tone = right ? 'var(--sage)' : wrong ? 'var(--clay)' : on ? 'var(--sage)' : 'var(--line-2)';
          const bg = right ? 'var(--sage-tint)' : wrong ? 'var(--clay-soft)' : on ? 'var(--sage-tint)' : 'var(--card)';
          const filled = on || right;
          return (
            <button
              key={oi}
              type="button"
              disabled={review}
              onClick={() => onPick(oi)}
              style={{ all: 'unset', cursor: review ? 'default' : 'pointer', display: 'block' }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px', borderRadius: 12, border: `1.5px solid ${tone}`, background: bg, transition: 'border-color .14s, background .14s' }}>
                <div style={{ width: 22, height: 22, flex: 'none', borderRadius: multi ? 6 : '50%', border: `1.6px solid ${filled ? tone : 'var(--line-2)'}`, background: filled ? tone : 'transparent', display: 'grid', placeItems: 'center', color: 'var(--on-accent)' }}>
                  {filled && <Icon name={wrong ? 'x' : 'check'} size={13} stroke={3} />}
                </div>
                <span style={{ flex: 1, fontSize: 15.5, fontWeight: filled ? 500 : 400 }}>{opt}</span>
                {right && <Icon name="check" size={16} style={{ color: 'var(--sage)' }} />}
                {wrong && <Icon name="x" size={16} style={{ color: 'var(--clay)' }} />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
